// src/DatabaseCompaniesPage.jsx

import React, { useState, useEffect, useMemo } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';

// CompanyForm: Create or update
function CompanyForm({ initialData, onCancel, onSaved }) {
  const data = initialData || {};

  const {
    _id,
    companyName: initName = '',
    personInCharge: initPIC = '',
    fleetSize: initFleet = '',
    address: initAddress = '',
    phoneNumber: initPhone = '',
    ships: initShips = [],
  } = data;

  // 1️⃣ Normalize initial ship IDs into strings
  const assignedShipIds = useMemo(() => {
    return initShips
      .map(s => {
        if (typeof s === 'string') return s;
        if (s && typeof s === 'object' && '_id' in s) return s._id.toString();
        return null;
      })
      .filter(Boolean);
  }, [initShips]);

  // 2️⃣ Master list of all ships
  const [shipsList, setShipsList] = useState([]);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_BASE_URL}/api/ships`)
      .then(r => r.json())
      .then(setShipsList)
      .catch(err => console.error('Failed to load ships', err));
  }, []);

  // 3️⃣ Local state of selected ship IDs for this form
  const [selectedShipIds, setSelectedShipIds] = useState(assignedShipIds);
  useEffect(() => {
    setSelectedShipIds(assignedShipIds);
  }, [assignedShipIds]);

  // 4️⃣ Compute the assigned ships objects
  const assignedShips = useMemo(() => {
    return shipsList.filter(ship =>
      selectedShipIds.includes(ship._id.toString())
    );
  }, [shipsList, selectedShipIds]);

  // 5️⃣ Form fields state
  const [company, setCompany] = useState({
    companyName: initName,
    personInCharge: initPIC,
    fleetSize: initFleet,
    address: initAddress,
    phoneNumber: initPhone,
  });

  // Reset form fields when editing a different company
  useEffect(() => {
    setCompany({
      companyName: initName,
      personInCharge: initPIC,
      fleetSize: initFleet,
      address: initAddress,
      phoneNumber: initPhone,
    });
  }, [initName, initPIC, initFleet, initAddress, initPhone]);

  const handleChange = e => {
    const { name, value } = e.target;
    setCompany(prev => ({ ...prev, [name]: value }));
  };

  // 6️⃣ Remove one ship from the assignment
  const removeShip = idToRemove => {
    setSelectedShipIds(prev => prev.filter(id => id !== idToRemove));
  };

  // 7️⃣ Submit (POST or PUT)
  const handleSubmit = async e => {
    e.preventDefault();
    const payload = { ...company, ships: selectedShipIds };
    const url = _id
      ? `${process.env.REACT_APP_API_BASE_URL}/api/companies/${_id}`
      : `${process.env.REACT_APP_API_BASE_URL}/api/companies`;
    const method = _id ? 'PUT' : 'POST';

    try {
      await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      onSaved();
    } catch (err) {
      console.error('Save failed', err);
    }
  };

  return (
    <div className="container-fluid mt-4">
      <div className="card-header">
        {_id ? 'Update Company' : 'New Company'}
      </div>
      <div className="card-body">
        <form onSubmit={handleSubmit}>
          {/* Company Name & Person In Charge */}
          <div className="row mb-3">
            <div className="col">
              <label>Company Name</label>
              <input
                name="companyName"
                value={company.companyName}
                onChange={handleChange}
                className="form-control"
                required
              />
            </div>
            <div className="col">
              <label>Person In Charge</label>
              <input
                name="personInCharge"
                value={company.personInCharge}
                onChange={handleChange}
                className="form-control"
              />
            </div>
          </div>

          {/* Fleet Size & Phone Number */}
          <div className="row mb-3">
            <div className="col">
              <label>Fleet Size</label>
              <input
                name="fleetSize"
                type="number"
                value={company.fleetSize}
                onChange={handleChange}
                className="form-control"
              />
            </div>
            <div className="col">
              <label>Phone Number</label>
              <input
                name="phoneNumber"
                value={company.phoneNumber}
                onChange={handleChange}
                className="form-control"
              />
            </div>
          </div>

          {/* Address */}
          <div className="row mb-3">
            <div className="col">
              <label>Address</label>
              <input
                name="address"
                value={company.address}
                onChange={handleChange}
                className="form-control"
              />
            </div>
          </div>

          {/* Assigned Ships with ❌ button */}
          <div className="row mb-3">
            <div className="col">
              <label>Current Assigned Ships</label>
              <ul
                className="list-group"
                style={{ maxHeight: 150, overflowY: 'auto' }}
              >
                {assignedShips.length > 0 ? (
                  assignedShips.map(ship => (
                    <li
                      key={ship._id}
                      className="list-group-item d-flex justify-content-between align-items-center"
                    >
                      {ship.vesselName || ship.name || 'Unnamed ship'}
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={() => removeShip(ship._id.toString())}
                      >
                        ×
                      </button>
                    </li>
                  ))
                ) : (
                  <li className="list-group-item">No ships assigned</li>
                )}
              </ul>
            </div>
          </div>

          {/* Submit & Cancel */}
          <button type="submit" className="btn btn-primary me-2">
            {_id ? 'Update' : 'Create'}
          </button>
          {_id && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onCancel}
            >
              Cancel
            </button>
          )}
        </form>
      </div>
    </div>
  );
}

// Main page
export default function DatabaseCompaniesPage() {
  const [companies, setCompanies] = useState([]);
  const [message, setMessage] = useState('');
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_BASE_URL}/api/companies`)
      .then(r => r.json())
      .then(setCompanies)
      .catch(() => setMessage('Failed to load companies'));
  }, []);

  const handleRowClick = c => setEditing(c);
  const handleDelete = id =>
    fetch(`${process.env.REACT_APP_API_BASE_URL}/api/companies/${id}`, { method: 'DELETE' })
      .then(() => setCompanies(cs => cs.filter(x => x._id !== id)))
      .catch(() => setMessage('Delete failed'));

  const handleFormSaved = () => {
    setEditing(null);
    fetch(`${process.env.REACT_APP_API_BASE_URL}/api/companies`)
      .then(r => r.json())
      .then(setCompanies)
      .catch(() => setMessage('Failed to reload companies'));
  };

  return (
    <div className="container mt-4">
      <h2>Companies Database</h2>
      {message && <div className="alert alert-info">{message}</div>}

      <CompanyForm
        initialData={editing}
        onCancel={() => setEditing(null)}
        onSaved={handleFormSaved}
      />

      <table className="table table-bordered">
        <thead>
          <tr>
            <th>#</th>
            <th>Company Name</th>
            <th>Person In Charge</th>
            <th>Fleet Size</th>
            <th>Address</th>
            <th>Phone Number</th>
            <th>Delete</th>
          </tr>
        </thead>
        <tbody>
          {companies.map((c, i) => (
            <tr
              key={c._id}
              onClick={() => handleRowClick(c)}
              style={{ cursor: 'pointer' }}
            >
              <td>{i + 1}</td>
              <td>{c.companyName}</td>
              <td>{c.personInCharge}</td>
              <td>{c.fleetSize}</td>
              <td>{c.address}</td>
              <td>{c.phoneNumber}</td>
              <td onClick={e => e.stopPropagation()}>
                <button
                  className="btn btn-sm btn-danger"
                  onClick={() => handleDelete(c._id)}
                >
                  ×
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
